import { ICellRendererParams } from "ag-grid-community";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { Badge, BadgeVariant } from "@/components/ui/Badge";
import { TableColumn } from "@/components/ui/TableV2";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { User } from "@/types/user";
import { Workspace } from "@/types/workspaces";

interface UseWorkspaceColumnsParams {
  accessibleWorkspaces?: { workspaces: Workspace[] };
  currentUser?: User;
}

export const useWorkspaceColumns = ({
  accessibleWorkspaces,
  currentUser,
}: UseWorkspaceColumnsParams) => {
  const { workspaceId } = useParams();
  const activeWorkspaceId = currentUser?.memberships.workspaces.find(
    workspace => workspace.workspace_id === Number(workspaceId),
  )?.organization_id;
  const defaultWorkspaceId = currentUser?.memberships.organizations.find(
    organization => organization.organization_id === activeWorkspaceId,
  )?.default_workspace_id;

  return useMemo(
    (): TableColumn<Workspace>[] => [
      {
        id: 1,
        name: "name",
        field: "name",
        header: "Name",
        minWidth: 250,
        cellRenderer: (params: ICellRendererParams<Workspace>) => (
          <div className="ml-2 flex items-center gap-2">
            <OverflowDisplayTooltip className="text-sm" content={params.data?.name}>
              {params.data?.name}
            </OverflowDisplayTooltip>
            {params.data?.id === defaultWorkspaceId && (
              <Badge variant={BadgeVariant.GRAY}>Default</Badge>
            )}
          </div>
        ),
      },
      {
        id: 2,
        name: "access",
        header: "Access",
        sortable: false,
        cellRenderer: (params: ICellRendererParams<Workspace>) =>
          accessibleWorkspaces?.workspaces.some(w => w.id === params.data?.id) ? "Yes" : "No",
      },
      {
        id: 3,
        name: "role",
        header: "Role",
        sortable: false,
        cellRenderer: (params: ICellRendererParams<Workspace>) => {
          const hasAccess = accessibleWorkspaces?.workspaces.some(w => w.id === params.data?.id);

          if (!hasAccess) {
            return "-";
          }

          return (
            <div className="flex items-center">
              <Badge
                variant={
                  params.data?.admin_user_ids.includes(currentUser?.id || 0)
                    ? BadgeVariant.BLUE
                    : BadgeVariant.GRAY
                }
              >
                {params.data?.admin_user_ids.includes(currentUser?.id || 0) ? "Admin" : "Member"}
              </Badge>
            </div>
          );
        },
      },
      {
        id: 4,
        name: "user_count",
        field: "user_count",
        header: "# of Members",
        cellRenderer: (params: ICellRendererParams<Workspace>) => params.data?.user_count,
      },
    ],
    [accessibleWorkspaces, currentUser, defaultWorkspaceId],
  );
};
